"use client";
import { createDialogComponent } from "../dialog/dialog.js";
import { usePopover } from "../popover/popover.js";
import { HovercardScopedContextProvider, useHovercardProviderContext } from "./hovercard-context.js";
import { getElementPolygon, getEventPoint, isPointInPolygon } from "./utils/polygon.js";
import { createElement, createHook, forwardRef, useBooleanEvent, useEvent, useIsMouseMoving, useLiveRef, useMergeRefs, usePortalRef, useSafeLayoutEffect, useWrapElement } from "@ariakit/react-utils";
import { addGlobalEventListener, chain, hasFocusWithin, invariant, isFalsyBooleanCallback } from "@ariakit/utils";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
import { useStoreState } from "@ariakit/react-store";
import { sync } from "@ariakit/store";
//#region src/hovercard/hovercard.tsx
const TagName = "div";
function isMovingOnHovercard(path, card, anchor, nested) {
	if (hasFocusWithin(card)) return true;
	if (path.includes(card)) return true;
	if (anchor && path.includes(anchor)) return true;
	if (nested?.some((card) => hasFocusWithin(card) || path.includes(card))) return true;
	return false;
}
function disableEventOnTransit({ event, element, enterPointRef, disablePointerEvents, refreshEnterPoint = false }) {
	const enterPoint = enterPointRef.current;
	if (!enterPoint) return false;
	const currentPoint = getEventPoint(event);
	if (!isPointInPolygon(currentPoint, getElementPolygon(element, enterPoint))) return false;
	if (refreshEnterPoint) enterPointRef.current = currentPoint;
	if (disablePointerEvents(event)) {
		event.preventDefault();
		event.stopPropagation();
	}
	return true;
}
function useAutoFocusOnHide({ store, ...props }) {
	const [autoFocusOnHide, setAutoFocusOnHide] = useState(false);
	const mounted = useStoreState(store, "mounted");
	useEffect(() => {
		if (!mounted) setAutoFocusOnHide(false);
	}, [mounted]);
	const onFocusProp = props.onFocus;
	const onFocus = useEvent((event) => {
		onFocusProp?.(event);
		if (event.defaultPrevented) return;
		setAutoFocusOnHide(true);
	});
	const finalFocusRef = useRef(null);
	useEffect(() => {
		return sync(store, ["anchorElement"], (state) => {
			finalFocusRef.current = state.anchorElement;
		});
	}, [store]);
	props = {
		autoFocusOnHide,
		finalFocus: finalFocusRef,
		...props,
		onFocus
	};
	return props;
}
const NestedHovercardContext = createContext(null);
/**
* Returns props to create a `Hovercard` component.
* @see https://ariakit.com/components/hovercard
* @example
* ```jsx
* const store = useHovercardStore();
* const props = useHovercard({ store });
* <HovercardAnchor store={store}>@username</HovercardAnchor>
* <Role {...props}>Details</Role>
* ```
*/
const useHovercard = createHook(function useHovercard({ store, modal = false, portal = modal, hideOnEscape = true, hideOnHoverOutside = true, disablePointerEventsOnApproach = !!hideOnHoverOutside, ...props }) {
	const context = useHovercardProviderContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "Hovercard must receive a `store` prop or be wrapped in a HovercardProvider component.");
	const ref = useRef(null);
	const nestedHovercardsRef = useRef([]);
	const hideTimeoutRef = useRef(0);
	const enterPointRef = useRef(null);
	const { portalRef, domReady } = usePortalRef(portal, props.portalRef);
	const isMouseMoving = useIsMouseMoving();
	const mayHideOnHoverOutside = !!hideOnHoverOutside;
	const hideOnHoverOutsideProp = useBooleanEvent(hideOnHoverOutside);
	const mayDisablePointerEvents = !!disablePointerEventsOnApproach;
	const disablePointerEventsProp = useBooleanEvent(disablePointerEventsOnApproach);
	const open = useStoreState(store, "open");
	const mounted = useStoreState(store, "mounted");
	const clearHideTimeout = useCallback(() => {
		window.clearTimeout(hideTimeoutRef.current);
		hideTimeoutRef.current = 0;
	}, []);
	useEffect(() => {
		if (!domReady) return;
		if (!mounted) return;
		if (!mayHideOnHoverOutside && !mayDisablePointerEvents) return;
		const element = ref.current;
		if (!element) return;
		const onMouseMove = (event) => {
			if (!store) return;
			if (!isMouseMoving()) return;
			const { anchorElement, hideTimeout, timeout } = store.getState();
			const path = event.composedPath();
			const anchor = anchorElement;
			if (isMovingOnHovercard(path, element, anchor, nestedHovercardsRef.current)) {
				enterPointRef.current = anchor && path.includes(anchor) ? getEventPoint(event) : null;
				clearHideTimeout();
				return;
			}
			if (hideTimeoutRef.current) return;
			if (disableEventOnTransit({
				event,
				element,
				enterPointRef,
				disablePointerEvents: disablePointerEventsProp,
				refreshEnterPoint: true
			})) return;
			if (!hideOnHoverOutsideProp(event)) return;
			hideTimeoutRef.current = window.setTimeout(() => {
				hideTimeoutRef.current = 0;
				store?.hide();
			}, hideTimeout ?? timeout);
		};
		return chain(addGlobalEventListener("mousemove", onMouseMove, true), clearHideTimeout);
	}, [
		store,
		isMouseMoving,
		domReady,
		mounted,
		mayHideOnHoverOutside,
		mayDisablePointerEvents,
		nestedHovercardsRef,
		clearHideTimeout,
		disablePointerEventsProp,
		hideOnHoverOutsideProp
	]);
	useEffect(() => {
		if (!domReady) return;
		if (!mounted) return;
		if (!mayDisablePointerEvents) return;
		const disableEvent = (event) => {
			const element = ref.current;
			if (!element) return;
			disableEventOnTransit({
				event,
				element,
				enterPointRef,
				disablePointerEvents: disablePointerEventsProp
			});
		};
		return chain(addGlobalEventListener("mouseenter", disableEvent, true), addGlobalEventListener("mouseover", disableEvent, true), addGlobalEventListener("mouseout", disableEvent, true), addGlobalEventListener("mouseleave", disableEvent, true));
	}, [
		domReady,
		mounted,
		mayDisablePointerEvents,
		disablePointerEventsProp
	]);
	useEffect(() => {
		if (!domReady) return;
		if (open) return;
		store?.setAutoFocusOnShow(false);
	}, [
		store,
		domReady,
		open
	]);
	const openRef = useLiveRef(open);
	useEffect(() => {
		if (!domReady) return;
		return () => {
			if (!openRef.current) store?.setAutoFocusOnShow(false);
		};
	}, [store, domReady]);
	const registerOnParent = useContext(NestedHovercardContext);
	useSafeLayoutEffect(() => {
		if (modal) return;
		if (!portal) return;
		if (!mounted) return;
		if (!domReady) return;
		const element = ref.current;
		if (!element) return;
		return registerOnParent?.(element);
	}, [
		modal,
		portal,
		mounted,
		domReady
	]);
	const registerNestedHovercard = useCallback((element) => {
		clearHideTimeout();
		nestedHovercardsRef.current = [...nestedHovercardsRef.current, element];
		const parentUnregister = registerOnParent?.(element);
		return () => {
			clearHideTimeout();
			nestedHovercardsRef.current = nestedHovercardsRef.current.filter((item) => item !== element);
			parentUnregister?.();
		};
	}, [clearHideTimeout, registerOnParent]);
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx(HovercardScopedContextProvider, {
		value: store,
		children: /* @__PURE__ */ jsx(NestedHovercardContext.Provider, {
			value: registerNestedHovercard,
			children: element
		})
	}), [store, registerNestedHovercard]);
	props = {
		...props,
		ref: useMergeRefs(ref, props.ref)
	};
	props = useAutoFocusOnHide({
		store,
		...props
	});
	const autoFocusOnShow = useStoreState(store, (state) => modal || state.autoFocusOnShow);
	props = usePopover({
		store,
		modal,
		portal,
		autoFocusOnShow,
		...props,
		portalRef,
		hideOnEscape(event) {
			if (isFalsyBooleanCallback(hideOnEscape, event)) return false;
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					store?.hide();
				});
			});
			return true;
		}
	});
	return props;
});
/**
* Renders a hovercard element, which is a popover that's usually made visible
* by hovering the mouse cursor over a
* [`HovercardAnchor`](https://ariakit.com/reference/hovercard-anchor).
* @see https://ariakit.com/components/hovercard
* @example
* ```jsx {3}
* <HovercardProvider>
*   <HovercardAnchor>@username</HovercardAnchor>
*   <Hovercard>Details</Hovercard>
* </HovercardProvider>
* ```
*/
const Hovercard = createDialogComponent(forwardRef(function Hovercard(props) {
	return createElement(TagName, useHovercard(props));
}), useHovercardProviderContext);
//#endregion
export { Hovercard, useHovercard };

//# sourceMappingURL=hovercard.js.map